import { useState } from 'react';
import {
  useQueryParams,
  StringParam,
  NumberParam,
} from 'use-query-params';
import Container from '@mui/material/Container';
import useGetJobs from 'hooks/useJobs';
import usePermissions from 'hooks/usePermissions';
import useAccountStore from 'store/accountStore';
import { getSavedAccountMode } from 'utils/helpers';
import { auth, useAuthState } from 'api/services/firebase';
import withSeo from 'components/hoc/withSeo';
import useLocalStorage from 'hooks/useLocalStorage';
import useDebouncedState from 'hooks/useDebouncedState';
import TeamJobs from './TeamJobs';
import UserJobs from './UserJobs';

const JobsPage = () => {
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    perPage: StringParam,
    membersIds: StringParam,
    profile: StringParam,
    search: StringParam,
    runStatus: StringParam,
  });
  const [storedValue, setValue] = useLocalStorage('jobs_table_row', 10);
  const [user] = useAuthState(auth);
  const {
    page = 1,
    perPage = storedValue,
    runStatus,
    ...otherFilters
  } = query;

  // Parse comma-separated status values into an array
  const parsedStatus = runStatus ? runStatus.split(',') : undefined;

  const filters = {
    ...otherFilters,
    runStatus: parsedStatus,
  };

  const teamId = useAccountStore(
    (state) => state.activeAccount.teamId,
  );

  const { hasPermission } = usePermissions();

  const {
    jobs,
    total,
    isLoading,
    refetch,
    isRefetching,
  } = useGetJobs(page - 1, perPage, filters);

  const [searchQuery, setSearchQuery] = useState(query.search || '');
  const debouncedSearchQuery = useDebouncedState(searchQuery, 1);

  const onPageChange = (clickedPage) => {
    setQuery({ ...query, page: clickedPage + 1, profile: getSavedAccountMode(user?.uid) });
  };

  const onRowsPerPageChange = (clickedPerPage) => {
    setValue(clickedPerPage);
    setQuery({ perPage: clickedPerPage, page: 1, profile: getSavedAccountMode(user?.uid) });
  };

  const onFilterChange = (filterName, filterValue) => {
    // If the filter is 'status', use 'runStatus' in the query params
    const queryParamName = filterName === 'status' ? 'runStatus' : filterName;
    setQuery({
      ...query, [queryParamName]: filterValue, page: 1, profile: getSavedAccountMode(user?.uid),
    });
  };
  const handleSearchInputChange = (searchQueryString) => {
    const searchQueryValue = searchQueryString.trim();
    setSearchQuery(searchQueryValue);

    setQuery({
      ...query,
      search: searchQueryValue || undefined,
      page: 1,
      profile: getSavedAccountMode(user?.uid),
    });
  };

  return (
    <Container>
      {hasPermission('jobs.team') ? (
        <TeamJobs
          jobs={jobs}
          total={total}
          page={page - 1}
          perPage={perPage}
          isLoading={isLoading}
          refetch={refetch}
          isRefetching={isRefetching}
          filters={filters}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          onFilterChange={onFilterChange}
          handleSearch={handleSearchInputChange}
          searchQuery={debouncedSearchQuery}
        />
      ) : (
        <UserJobs
          jobs={jobs}
          total={total}
          page={page - 1}
          perPage={perPage}
          isLoading={isLoading}
          refetch={refetch}
          isRefetching={isRefetching}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          onFilterChange={onFilterChange}
          filters={filters}
          handleSearch={handleSearchInputChange}
          searchQuery={debouncedSearchQuery}
        />
      )}
    </Container>
  );
};

export default withSeo(JobsPage);
